import "@/styles/components/scroll-reveal.css";
import { animate, inView, stagger } from "motion";

const REVEAL_MARGIN = "0px 0px -20% 0px";
const STAGGER_NORMAL = 0.1;
const STAGGER_DENSE = 0.03;
const DENSE_STAGGER_THRESHOLD = 15; // dense stagger keeps total time bounded for big lists
const DEFAULT_DURATION = 0.7;
const SLOW_DURATION = 1.1;
const SEQUENCE_DURATION = 0.8;
const SEQUENCE_OVERLAP = -0.4; // each sequence segment starts 0.4s before prior ends

// Nested list expansion: a single `data-sr` parent wrapping a list is replaced
// by per-item reveals so each <li> animates individually as fade-up.
const NESTED_LIST_SELECTOR = "ol, ul.as-list, [class*=as-checklist-]";

const VARIANTS = {
  "fade-up": { keyframes: { opacity: 1, translate: ["0px 40px", "0px 0px"] } },
  "slide-up": { keyframes: { translate: ["0px 40px", "0px 0px"] } },
  "fade-up-left": { keyframes: { opacity: 1, translate: ["50px 40px", "0px 0px"] } },
  "fade-down": { keyframes: { opacity: 1, translate: ["0px -40px", "0px 0px"] } },
  "fade-left": { keyframes: { opacity: 1, translate: ["40px 0px", "0px 0px"] } },
  "fade-left-slow": { keyframes: { opacity: 1, translate: ["20px 0px", "0px 0px"] }, duration: SLOW_DURATION },
  "fade-right": { keyframes: { opacity: 1, translate: ["-50px 0px", "0px 0px"] } },
  "slide-left": { keyframes: { translate: ["50px 0px", "0px 0px"] } },
  "slide-right": { keyframes: { translate: ["-50px 0px", "0px 0px"] } },
  "fade-in": { keyframes: { opacity: 1 }, duration: SLOW_DURATION },
  "scale-in": { keyframes: { opacity: 1, scale: [0, 1] }, duration: SLOW_DURATION },
  "scale-in-sm": { keyframes: { opacity: 1, scale: [0.95, 1] } },
};

const isVisible = (el) => el.checkVisibility() && !el.closest('[aria-hidden="true"]');

// Tracks elements with an in-flight animate() so a mid-flight mutation
// can't rebucket and restart them before data-sr-done is set.
const running = new WeakSet();

const processTouches = (group) => {
  group.querySelectorAll('[data-sr="touch"]:not([data-sr-done])').forEach((el) => {
    if (running.has(el)) return;
    if (!isVisible(el)) return;
    el.setAttribute("data-sr-touched", "");
    el.setAttribute("data-sr-done", "");
  });
};

const expandChildReveals = (group) => {
  group.querySelectorAll("[data-sr]:not([data-sr-done])").forEach((el) => {
    const isSubtree = el.dataset.sr === "subtree";
    const list = el.querySelector(NESTED_LIST_SELECTOR);

    if (!isSubtree && !list) return;
    if (el.querySelector(".ignore-sr")) return;
    if (!isVisible(isSubtree ? el : list)) return;

    const targets = [];

    Array.from(el.children).forEach((child) => {
      if (child.matches(NESTED_LIST_SELECTOR)) {
        targets.push(...child.querySelectorAll("li"));
      } else {
        targets.push(child);
      }
    });

    targets.forEach((t) => t.setAttribute("data-sr", "fade-up"));

    el.removeAttribute("data-sr");
  });
};

// Group eligible elements under `group` by their variant key.
const bucketByVariant = (group) => {
  const buckets = new Map();

  group.querySelectorAll("[data-sr]:not([data-sr-done])").forEach((el) => {
    const key = el.dataset.sr;

    if (!VARIANTS[key]) return;
    if (running.has(el)) return;
    if (!isVisible(el)) return;

    if (!buckets.has(key)) buckets.set(key, []);

    buckets.get(key).push(el);
  });

  return buckets;
};

// Animate one variant bucket; mark each element done when finished.
const animateBucket = async (els, variant) => {
  const staggerAmount = els.length > DENSE_STAGGER_THRESHOLD ? STAGGER_DENSE : STAGGER_NORMAL;

  els.forEach((el) => running.add(el));

  return animate(els, variant.keyframes, {
    duration: variant.duration ?? DEFAULT_DURATION,
    delay: stagger(staggerAmount),
  }).then(() => {
    els.forEach((el) => {
      el.setAttribute("data-sr-done", "");
      running.delete(el);
    });
  });
};

// Animate every bucket in `group`, then mark the group done (except observe
// groups — they keep CSS hidden state active so AJAX-injected children fade in).
const animateGroup = async (group) => {
  processTouches(group);
  expandChildReveals(group);

  const buckets = bucketByVariant(group);
  const runs = [];
  buckets.forEach((els, key) => runs.push(animateBucket(els, VARIANTS[key])));

  return Promise.all(runs).then(() => {
    if (group.dataset.srGroup !== "observe") {
      group.setAttribute("data-sr-done", "");
    }
  });
};

// Run a `data-sr-group="sequence"` group as one ordered animation. Each
// segment starts SEQUENCE_OVERLAP seconds before the prior ends.
const animateSequence = async (group) => {
  processTouches(group);

  const segments = [];

  group.querySelectorAll("[data-sr]:not([data-sr-done])").forEach((el) => {
    const variant = VARIANTS[el.dataset.sr];
    if (!variant || running.has(el) || !isVisible(el)) return;

    segments.push([el, variant.keyframes, { duration: SEQUENCE_DURATION, at: SEQUENCE_OVERLAP }]);
  });

  if (!segments.length) {
    group.setAttribute("data-sr-done", "");
    return Promise.resolve();
  }

  segments.forEach(([el]) => running.add(el));

  return animate(segments).then(() => {
    segments.forEach(([el]) => {
      el.setAttribute("data-sr-done", "");
      running.delete(el);
    });
    group.setAttribute("data-sr-done", "");
  });
};

const groupState = new WeakMap();
const stateFor = (group) => {
  let state = groupState.get(group);
  if (!state) {
    state = { inView: false, registered: false, observer: null, revealQueued: false };
    groupState.set(group, state);
  }
  return state;
};

const reveal = (group) => {
  const runAnimation = () => (group.dataset.srGroup === "sequence" ? animateSequence(group) : animateGroup(group));

  // `onload`: animate immediately, no viewport gate
  if (group.dataset.srGroup === "onload") {
    runAnimation();
    return;
  }

  const state = stateFor(group);

  // Already in view (from a prior inView fire): mutations should animate
  // newly-added elements right away rather than wait for re-entry.
  if (state.inView) {
    runAnimation();
    return;
  }

  // First-touch: register inView exactly once per group.
  if (!state.registered) {
    state.registered = true;
    inView(
      group,
      () => {
        state.inView = true;
        runAnimation();
      },
      { margin: REVEAL_MARGIN },
    );
  }
};

export default {
  init: () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    document.querySelectorAll("[data-sr-group]").forEach((group) => {
      // Kill-switch ancestor: strip attr so any external `reveal()` is a no-op too.
      if (group.closest("[data-disable-animations]")) {
        group.removeAttribute("data-sr-group");
        return;
      }

      const type = group.dataset.srGroup;

      // External trigger: caller invokes reveal(group) on its own schedule.
      if (type === "trigger") return;

      // Async children: re-scan on childList changes and aria-hidden flips.
      if (type === "observe") {
        const state = stateFor(group);
        if (!state.observer) {
          state.observer = new MutationObserver(() => {
            if (state.revealQueued) return;
            state.revealQueued = true;
            requestAnimationFrame(() => {
              state.revealQueued = false;
              reveal(group);
            });
          });
          state.observer.observe(group, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["aria-hidden"],
          });
        }
      }

      reveal(group);
    });
  },
  reveal,
};
